/**
 * useLanguagePort — port-factory hook returning the app's `LanguagePort`
 * (T015; persistent member setting since 059).
 *
 * Replaces the previous `LanguageProvider` React component. The move from
 * component to hook is part of Shape B of T015's composition consolidation:
 * the composer (`main.tsx` T024 / `renderAppWithPorts` T016) builds all
 * ports via factory hooks and hands them to the single `PortsProvider`.
 * That keeps the layering honest — port-creation in a hook, port-provision
 * in the composition-root component — and removes the awkward nesting of
 * `<LanguageProvider>` around `<PortsProvider>` the component shape would
 * otherwise force.
 *
 * Implements the `LanguagePort` contract
 * (`specs/059-member-language-setting/contracts/language-port.md`):
 *
 *   1. Finnish default on every fresh mount with no stored member choice.
 *      When `deps.session` is already a member carrying a stored `language`,
 *      the first render starts in it.
 *   2. Supported set — `['fi', 'en', 'sv'] as const`, module-scope for identity
 *      stability.
 *   3. Switch — `set(next)` synchronously mirrors `<html lang>`, updates
 *      React state (triggering re-render of consumers of `useLanguage`),
 *      and fans out to listeners registered through `subscribe`. No
 *      listener fires on registration. `set(active)` (same-value no-op)
 *      is a full no-op: no DOM write, no React update, no subscriber
 *      notification — the ref-based equality check in the hook gates
 *      everything behind a real transition.
 *   4. Member setting — a signed-in member's switch is handed to
 *      `deps.save` (the server); the member's stored choice arrives back on
 *      the session (`session.me`) and is applied when they sign in, with no
 *      write. Signing out returns to Finnish. Anonymous and admin switches
 *      stay in memory. Zero reads/writes to `localStorage`,
 *      `sessionStorage`, or `document.cookie`, and the browser's own
 *      language settings are never read.
 *   5. Unsubscribe — idempotent via `Set.delete` (no-op on the second
 *      call); scoped to the specific listener (closure-bound).
 *
 * `useRef<Set<...>>` holds the listener roster so subscription mutations do
 * NOT trigger re-renders — they are a consumer-side concern, not a render
 * concern. `useMemo` wraps the returned port object so its identity only
 * changes when `active` does (stable effect dependencies downstream).
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type { LanguageCode, LanguagePort } from '../i18n/LanguagePort.js';
import type { AuthState, SessionPort } from '../session/SessionPort.js';

const SUPPORTED: readonly LanguageCode[] = ['fi', 'en', 'sv'] as const;

export interface UseLanguagePortDeps {
  /** The session whose member carries a stored language (`session.me`). */
  readonly session?: SessionPort;
  /** Stores a signed-in member's switch on the server (`member.language.set`). */
  readonly save?: (next: LanguageCode) => Promise<unknown>;
}

function storedLanguage(state: AuthState): LanguageCode | undefined {
  return state.kind === 'member' ? state.language : undefined;
}

export function useLanguagePort(deps: UseLanguagePortDeps = {}): LanguagePort {
  const { session, save } = deps;
  const [active, setActive] = useState<LanguageCode>(
    () => (session === undefined ? undefined : storedLanguage(session.state)) ?? 'fi',
  );

  const listenersRef = useRef<Set<(next: LanguageCode) => void>>(new Set());
  // Source of truth for the "did the locale actually change?" check. The
  // ref mirrors `active` but is updated *inside* `transition` so successive
  // `set('en')` calls in the same tick still collapse to one dispatch —
  // reading from `active` directly would see the stale closure value on
  // back-to-back calls before React's next render commits.
  const activeRef = useRef<LanguageCode>(active);
  // Read by `set` at call time so its identity stays stable across renders.
  const sessionRef = useRef(session);
  const saveRef = useRef(save);
  // Member writes run one after another, so the server ends on the last choice
  // even when an earlier request is slower.
  const writeChainRef = useRef<Promise<unknown>>(Promise.resolve());

  // Mount + every-change DOM mirror. `<html lang>` must reflect the active
  // locale (spec FR-041, a11y "Language declaration"). Pinning the mirror
  // here AND inside `set` below means the initial mount correctly writes
  // `lang="fi"` even if no consumer ever calls `set`; `set` covers the
  // synchronous live-edit path.
  useEffect(() => {
    if (typeof document !== 'undefined') {
      document.documentElement.lang = active;
    }
  }, [active]);

  // react-compiler-exception: identity feeds `set` and the session effect below; both must stay stable
  const transition = useCallback((next: LanguageCode): boolean => {
    // Empty dep array on this `useCallback` is intentional and safe:
    //   - `activeRef.current` is always fresh (ref, not state).
    //   - `setActive` from `useState` is stable across renders (React
    //     guarantees the same setter identity for the lifetime of the
    //     component).
    //   - `listenersRef.current` is a mutable Set referenced through a
    //     stable ref.
    // If new render-dependent values are ever captured here, they must
    // be added to the dependency array.
    //
    // Same-value no-op: `set(active)` does NOT mirror the DOM, does NOT
    // schedule a React update, and does NOT notify subscribers —
    // matching `SessionPort`'s "notified once per explicit transition"
    // posture. A persistence adapter calling `set(storedLocale)` on
    // mount with the current value stays quiet; only a genuine switch
    // fans out.
    if (activeRef.current === next) {
      return false;
    }
    activeRef.current = next;

    // Synchronous DOM mirror — the contract requires `<html lang>` to
    // reflect the new value *before* any consumer observes the transition.
    if (typeof document !== 'undefined') {
      document.documentElement.lang = next;
    }

    setActive(next);

    // Snapshot the listener roster before iterating so a listener that
    // unsubscribes itself during its own call does not mutate the live
    // iterator — the idempotent-unsubscribe contract would otherwise trip
    // here. Dispatch happens outside any `setActive` updater so React's
    // StrictMode dev-time double-invoke of state reducers does NOT
    // double-fire listeners.
    for (const listener of Array.from(listenersRef.current)) {
      listener(next);
    }
    return true;
  }, []);

  // react-compiler-exception: identity feeds the useMemo LanguagePort below + subscribe contract
  const set = useCallback(
    (next: LanguageCode) => {
      if (!transition(next)) {
        return;
      }
      const persist = saveRef.current;
      if (persist === undefined || sessionRef.current?.state.kind !== 'member') {
        return;
      }
      // A failed write keeps the choice for this visit; it is just not
      // remembered. There is no client logger to report it to.
      writeChainRef.current = writeChainRef.current
        .then(() => persist(next))
        .catch(() => undefined);
    },
    [transition],
  );

  useEffect(() => {
    sessionRef.current = session;
    saveRef.current = save;
  }, [session, save]);

  // Apply the member's stored choice when they sign in (or it changes on the
  // server), and return to Finnish when they sign out. Compared against the
  // previous state, so a member's own unsaved-yet switch is not undone by an
  // unrelated session notification that still carries the older value.
  useEffect(() => {
    if (session === undefined) {
      return;
    }
    let previous: AuthState | undefined;
    const reconcile = (state: AuthState): void => {
      const before = previous;
      previous = state;
      if (state.kind === 'member') {
        const stored = storedLanguage(state);
        if (
          stored !== undefined &&
          (before?.kind !== 'member' || stored !== storedLanguage(before))
        ) {
          transition(stored);
        }
      } else if (before?.kind === 'member') {
        transition('fi');
      }
    };
    reconcile(session.state);
    return session.subscribe(reconcile);
  }, [session, transition]);

  // react-compiler-exception: subscribe identity is the external-store contract for LanguagePort consumers
  const subscribe = useCallback((listener: (next: LanguageCode) => void) => {
    listenersRef.current.add(listener);
    return () => {
      // Idempotent: `Set.delete` is a no-op after the first successful
      // removal, so calling the returned closure twice is safe (contract 5).
      listenersRef.current.delete(listener);
    };
  }, []);

  // react-compiler-exception: port object identity changes only on locale switch — downstream effect-dependency contract
  return useMemo<LanguagePort>(
    () => ({
      active,
      supported: SUPPORTED,
      set,
      subscribe,
    }),
    [active, set, subscribe],
  );
}
